import styled from 'styled-components'
import { t } from 'ttag'
import { useCallback, useState } from 'react'

import { Headed2LogoChevronIcon } from '@h2/components/icons/headed2LogoChevron'
import { Text } from '@h2/components/typography/text'
import { PB90T, PB97T, PB40T, WHITE, BLACK, SG90T, PG } from '@h2/components/colors'
import { helveticaNeueFont } from '@h2/components/mixins'
import { useNavigator } from '@h2/common/components/hooks/useNavigator'
import navigatorAccountSvg from '@h2/assets/svg/illustrations/navigatorAccount.svg'
import { Flex } from '@h2/components/layout/flex'

import { RecommendationDetail } from './recommendationDetail'
import { OutputChip } from './outputChip'

import { features } from '../../../../../components/media'

const ContentWrap = styled.div`
  padding: 12px;
  height: 160px;
  @media (${features.maxWidth.widescreen}) {
    height: 210px;
  }
  display: flex;
  flex-direction: column;
`
const FooterWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${PB97T};
  padding: 10px 0;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;

  > :first-child {
    ${helveticaNeueFont}
  }
`
const CardWrapper = styled.a`
  width: 100%;
  min-width: 0;
  border: none;
  display: block;
  margin: auto;
  border-radius: 6px;
  outline: ${({ type }) =>
    type === 'promotion' ? `1px ${PG} solid ` : `1px ${PB90T} solid `};
  background-color: ${({ type }) => (type === 'promotion' ? SG90T : WHITE)};
  background-size: 30%;
  background-repeat: no-repeat;
  background-position: center left;
  transition: all 0.3s ease-in-out;

  &,
  &:link,
  &:active,
  &:visited {
    text-decoration: none;
  }

  &:hover {
    text-decoration: none;
    outline: ${PB40T} solid 2px;
    ${FooterWrap} {
      background-color: ${PB90T};
    }
  }
`
const ChipWrap = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  gap: 3px;
  flex-wrap: wrap;
  > :first-child {
    margin-right: 6px;
  }

  > :nth-child(n),
  & * {
    cursor: pointer;
  }
`
const StyledImage = styled.img`
  max-width: 35%;
  float: right;
  margin: 6px;
`
const CardContainedImage = styled.img`
  max-width: 40%;
  margin: 6px auto;
`
const RecommendationCard = props => {
  const [modalOpen, setModalOpen] = useState(false)
  const navigator = useNavigator()
  const {
    recommendation: {
      name: heading,
      objectId,
      description: text,
      url,
      type,
      history,
      newSinceLastLogin
    }
  } = props
  const openModal = useCallback(() => {
    if (type !== 'promotion') {
      setModalOpen(true)
    } else {
      navigator(url)
    }
  }, [url, navigator, type])
  const closeModal = () => setModalOpen(false)
  const openKeypress = useCallback(
    evt => {
      if (evt.key === 'Enter') {
        openModal()
      }
    },
    [openModal]
  )

  if (!type) {
    return null
  }

  const recBy = history?.length ? t`Navigator` : t`Headed2`

  return (
    <>
      <CardWrapper tabIndex={0} type={type} onClick={openModal} onKeyUp={openKeypress}>
        <ContentWrap>
          <Text
            margin="0 0 5px 0"
            weight="bold"
            color={newSinceLastLogin ? BLACK : PB40T}>
            {heading}
          </Text>
          <Text size="small" color={PB40T} margin="0 0 5px 0">
            {type === 'promotion' && (
              <StyledImage
                src="/js/app/svg/70afa99c1895f61f150d.svg"
                alt={t`Promoted Activity`}
              />
            )}
            {type === 'navigator' && (
              <Flex alignItems="center">
                <CardContainedImage src={navigatorAccountSvg} />
              </Flex>
            )}
            {text || ''}
          </Text>
          {type !== 'promotion' && (
            <ChipWrap>
              <OutputChip text={type} shouldCapitalize={true} />
              <OutputChip recBy={recBy} text={t`Recommender: ${recBy}`} />
            </ChipWrap>
          )}
        </ContentWrap>
        <FooterWrap>
          <Text size="small" margin="0 8px 0 0" weight="bold">
            {type !== 'promotion' ? t`View Recommendation` : t`Learn More`}
          </Text>
          <Headed2LogoChevronIcon height="15px" width="15px" />
        </FooterWrap>
      </CardWrapper>
      <RecommendationDetail
        objectId={objectId}
        objectType={type}
        modalOpen={modalOpen}
        toggleModal={closeModal}
      />
    </>
  )
}

export { RecommendationCard }
